import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import { faShare } from "@outschool/icons";
import { useLocalPriceDisplay, useTranslation } from "@outschool/localization";
import { createPathWithActivity } from "@outschool/routes";
import { useProductClicked } from "@outschool/ui-analytics";
import {
  TrackedButton,
  UserHeadshotImage
} from "@outschool/ui-components-shared";
import {
  StarRatings,
  useActivityString
} from "@outschool/ui-components-website";
import { useLinkComponent } from "@outschool/ui-utils";
import * as React from "react";

import { useIsActivitySavedByCurrentUser } from "../../saving/graphql/hooks";
import { SaveHeart } from "../../saving/SaveButton";
import { CoinIcon } from "../../subscriptions/CoinIcon";
import { useShowCredits } from "../../subscriptions/hooks/useShowCredits";
import TileInfoBox from "../components/TileInfoBox";
import { CARD_COLUMN_WIDTH, CARD_MARGIN_RIGHT } from "./constants";

import type { CardProps } from "./CardProps";

export const TutorCard = ({
  activity,
  leader,
  openInNewTab = false,
  trackingProperties,
  shareButtonFunction,
  overrideShowCredits,
  columnWidth
}: CardProps) => {
  const Link = useLinkComponent();
  const theme = useTheme();
  const { t } = useTranslation("client\\components\\listings\\ListingCard");
  const trackProductClicked = useProductClicked();

  const { ageRangeString } = useActivityString();
  const { makePerMeetingPriceText, makePerMeetingPriceCreditsText } =
    useLocalPriceDisplay();

  const linkTo = createPathWithActivity(activity);
  const isSavedByUser = useIsActivitySavedByCurrentUser(activity);

  const customOnClick = React.useCallback(() => {
    if (leader) {
      trackProductClicked({ activity, leader });
    }
  }, [trackProductClicked, activity, leader]);

  const showCredits = useShowCredits() || !!overrideShowCredits;

  const topPriceText = showCredits
    ? makePerMeetingPriceCreditsText(activity)
    : makePerMeetingPriceText(activity);

  const cardColumnWidth = columnWidth || CARD_COLUMN_WIDTH;

  return (
    <Box sx={{ position: "relative" }}>
      {shareButtonFunction && (
        <TrackedButton
          trackingName="carousel card share button clicked"
          trackingUniqueId={activity?.uid}
          variant="outlined"
          onClick={shareButtonFunction}
          sx={{
            position: "absolute",
            left: cardColumnWidth - 50,
            top: 5,
            width: 30,
            padding: "0.7rem 1.9rem"
          }}
        >
          <Icon icon={faShare} size="medium" />
        </TrackedButton>
      )}
      <Link
        style={{
          display: "flex",
          width: cardColumnWidth,
          position: "relative",
          justifyContent: "flex-start",
          cursor: "pointer",
          marginRight: CARD_MARGIN_RIGHT,
          textDecoration: "none",
          marginTop: 73
        }}
        to={linkTo}
        {...trackingProperties}
        // @ts-ignore TS(2769): No overload matches this call.
        onClick={customOnClick}
        trackViews={true}
        target={openInNewTab ? "_blank" : undefined}
      >
        <Box
          sx={{
            backgroundColor: "#FFF",
            border: "1px solid #DDDFE3",
            borderRadius: 8,
            color: "#2E3238",
            height: 339,
            width: cardColumnWidth,
            "&:hover": {
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)"
            }
          }}
        >
          <Box sx={{ padding: "16px" }}>
            <Box
              sx={{
                height: 142,
                width: 142,
                borderRadius: 71,
                padding: 2,
                border: "2px solid #F7F7F7",
                backgroundColor: "white",
                position: "absolute",
                top: -73,
                left: 50
              }}
            >
              <UserHeadshotImage user={leader || {}} size={134} />
              {!!leader?.reviewCount && (
                <Box
                  flex
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    width: 132,
                    height: 32,
                    borderRadius: 16,
                    border: "2px solid #F7F7F7",
                    backgroundColor: "white",
                    position: "absolute",
                    top: 112,
                    left: 5
                  }}
                >
                  <StarRatings
                    size="0.7em"
                    rating={leader?.averageActivityStarRating || 0}
                  />
                  <Typography
                    as={Box}
                    variant="body2"
                    sx={{ color: "neutral.600", marginLeft: "0.2em" }}
                  >
                    ({leader?.reviewCount?.toLocaleString() || t`0`})
                  </Typography>
                </Box>
              )}
            </Box>
            <SaveHeart
              activity={activity}
              isSet={isSavedByUser}
              disabled={false}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                fontSize: 24
              }}
              trackingProperties={trackingProperties}
            />
            <Box
              sx={{
                fontSize: 16,
                fontWeight: "fontWeightBold",
                marginTop: "67px",
                marginBottom: "8px",
                display: "-webkit-box",
                WebkitLineClamp: "3",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                maxHeight: "calc(1.5em * 3)"
              }}
            >
              {activity.titleTranslated}
            </Box>
            <Box
              sx={{
                fontSize: 14,
                marginTop: "4px",
                color: "neutral.800",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                maxHeight: "calc(1.5em * 2)"
              }}
            >
              {leader?.name}
            </Box>
            <Box
              sx={{
                width: "calc(100% - 16px)",
                position: "absolute",
                bottom: "12px",
                left: "0px",
                marginX: "8px"
              }}
            >
              <Box
                sx={{
                  backgroundColor: "neutral.50",
                  textAlign: "center",
                  paddingY: 8,
                  borderRadius: 8,
                  borderBottomRightRadius: 8
                }}
              >
                <Typography as={Box} variant="h6">
                  {t`1-on-1 Tutoring`}
                </Typography>
              </Box>
              <Box
                flex
                sx={{
                  flexDirection: "row",
                  gap: "5px",
                  marginTop: "8px"
                }}
              >
                <TileInfoBox
                  topText={ageRangeString(activity, false)}
                  bottomText={t("Ages")}
                />
                {activity.duration_minutes && (
                  <TileInfoBox
                    topText={activity.duration_minutes.toString()}
                    bottomText={t("Mins")}
                  />
                )}
                <TileInfoBox
                  topText={
                    showCredits ? (
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignSelf: "center",
                            marginRight: 4
                          }}
                        >
                          <CoinIcon
                            height={16}
                            width={16}
                            fillColor={theme.palette.warning.main}
                            strokeColor={theme.palette.warning[700]!}
                          />
                        </Box>
                        {topPriceText}
                      </Box>
                    ) : (
                      topPriceText
                    )
                  }
                  bottomText={t`Per session`}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Link>
      {shareButtonFunction && (
        <Box>
          <Icon icon={faShare} />
        </Box>
      )}
    </Box>
  );
};
